import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Settings2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useBranchOptions } from '@/Pages/Reports/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState } from '@/components/ui/states';
import HealthBanner from './HealthBanner';
import ForecastChart from './ForecastChart';
import BacktestPanel from './BacktestPanel';
import SuggestionsPanel from './SuggestionsPanel';
import HolidaysPanel from './HolidaysPanel';
import { apiError, ForecastTarget, type ForecastHealth } from './lib';

const CHARTABLE_TARGETS: readonly ForecastTarget[] = [
  ForecastTarget.SALES,
  ForecastTarget.RESOURCE_UTILISATION,
];

const ALL_TARGETS: readonly ForecastTarget[] = [
  ForecastTarget.SALES,
  ForecastTarget.ITEM_DEMAND,
  ForecastTarget.PREP_DEMAND,
  ForecastTarget.LABOUR_DEMAND,
  ForecastTarget.INVENTORY_REPLENISHMENT,
  ForecastTarget.RESOURCE_UTILISATION,
];

/**
 * §23 / ADR-0031 — forecasting. One tab on Reports rather than a rail row of
 * its own (fifteen routes, all read through `forecasting.view`/`.manage`,
 * and every read here is a further cut of the same numbers Reports already
 * shows).
 *
 * `ITEM_DEMAND` and `PREP_DEMAND` can be generated from the trigger below
 * (they are real `ForecastTarget`s the backend runs), but the chart and
 * backtest above read one series per branch — those two targets are one
 * series PER ITEM or PER STATION, and picking one is a picker this pass does
 * not build (`GET /forecasting/points` with no `subjectId` returns every
 * item's points interleaved on the same dates, which is not a chart). The
 * two suggestion-only targets, `INVENTORY_REPLENISHMENT` and
 * `LABOUR_DEMAND`, never produce a point at all — see
 * `ForecastGenerationService.runInventorySuggestions`/`runLabourSuggestions`
 * (`pointsGenerated: 0`) — so they are represented in full by the
 * Suggestions panel below, not by a chart that would always be empty for them.
 */
export default function ForecastingTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('forecasting.manage');

  const { branches, loading: branchesLoading } = useBranchOptions();
  const [branchId, setBranchId] = useState('');
  const [target, setTarget] = useState<ForecastTarget>(ForecastTarget.SALES);
  const [triggerOpen, setTriggerOpen] = useState(false);

  const health = useApi<ForecastHealth>(branchId ? '/forecasting/health' : null, { branchId });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Field label={t('forecasting.branch')}>
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">
                {branchesLoading ? t('common.loading') : t('forecasting.pickBranch')}
              </option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="w-56">
          <Field label={t('forecasting.targetLabel')}>
            <Select value={target} onChange={(e) => setTarget(e.target.value as ForecastTarget)}>
              {CHARTABLE_TARGETS.map((tgt) => (
                <option key={tgt} value={tgt}>
                  {t(`forecasting.target.${tgt}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {canManage && branchId && (
          <Button variant="outline" size="sm" onClick={() => setTriggerOpen((v) => !v)}>
            <Icon as={Settings2} size="sm" className="me-1.5" />
            {t('forecasting.trigger.title')}
          </Button>
        )}
      </div>

      {!branchId ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('forecasting.pickBranchTitle')}
              hint={t('forecasting.pickBranchHint')}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <HealthBanner
            data={health.data}
            loading={health.loading}
            error={health.error}
            reload={health.reload}
          />

          {triggerOpen && canManage && (
            <TriggerForm
              branchId={branchId}
              onClose={() => setTriggerOpen(false)}
              onTriggered={() => {
                setTriggerOpen(false);
                health.reload();
              }}
            />
          )}

          <ForecastChart branchId={branchId} target={target} health={health.data} />
          <BacktestPanel branchId={branchId} target={target} />
          <SuggestionsPanel branchId={branchId} />
          <HolidaysPanel branchId={branchId} />
        </div>
      )}
    </div>
  );
}

/**
 * `POST /forecasting/runs` — outside the nightly sweep, for every target at
 * once (`TriggerForecastRunDto.targets` is an array). This is the one place
 * a manager reaches the two suggestion-only targets and the two per-subject
 * ones the chart above does not plot.
 */
function TriggerForm({
  branchId,
  onClose,
  onTriggered,
}: {
  branchId: string;
  onClose: () => void;
  onTriggered: () => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<ForecastTarget>>(new Set([ForecastTarget.SALES]));
  const [running, setRunning] = useState(false);

  const toggle = (tgt: ForecastTarget) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tgt)) next.delete(tgt);
      else next.add(tgt);
      return next;
    });
  };

  const submit = async () => {
    if (!selected.size) return;
    setRunning(true);
    try {
      await api.post('/forecasting/runs', { branchId, targets: [...selected] });
      toast.success(t('forecasting.trigger.triggered'));
      onTriggered();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.trigger.triggerFailed')));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-sm font-medium">{t('forecasting.trigger.pickTargets')}</p>
        <div className="flex flex-wrap gap-2">
          {ALL_TARGETS.map((tgt) => (
            <label
              key={tgt}
              className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent-soft"
            >
              <input
                type="checkbox"
                checked={selected.has(tgt)}
                onChange={() => toggle(tgt)}
                className="h-4 w-4 rounded border-line-strong"
              />
              {t(`forecasting.target.${tgt}`)}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={running || !selected.size}>
            {t('forecasting.trigger.run')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
